import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { useCart } from "@/lib/cart";
import { BloodDrip } from "@/components/BloodDrip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function pad(n) {
  return String(n).padStart(2, "0");
}

export default function ReleaseCountdown() {
  const [product, setProduct] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const { add, setOpen } = useCart();

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((r) => {
        const future = r.data
          .filter((p) => p.release_at && new Date(p.release_at).getTime() > Date.now())
          .sort((a, b) => new Date(a.release_at) - new Date(b.release_at));
        setProduct(future[0] || null);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const left = useMemo(() => {
    if (!product) return null;
    const ms = Math.max(0, new Date(product.release_at).getTime() - now);
    const s = Math.floor(ms / 1000);
    return {
      days: Math.floor(s / 86400),
      hours: Math.floor((s % 86400) / 3600),
      mins: Math.floor((s % 3600) / 60),
      secs: s % 60,
    };
  }, [product, now]);

  if (!product || !left) return null;

  const units = [
    { label: "DAYS", value: pad(left.days) },
    { label: "HOURS", value: pad(left.hours) },
    { label: "MINUTES", value: pad(left.mins) },
    { label: "SECONDS", value: pad(left.secs) },
  ];

  return (
    <section data-testid="release-countdown" className="relative py-24 sm:py-32 newsprint-grain overflow-hidden">
      <div className="absolute inset-0 blood-vignette pointer-events-none" />
      <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="relative mx-auto w-64 sm:w-80"
          >
            <div className="spotlight-frame aspect-[2/3] rotate-[-2deg]">
              <img src={product.cover} alt={`${product.title} cover`} className="w-full h-full object-cover" />
            </div>
            <span className="absolute -top-3 -right-3 bg-yell text-black font-marker text-xs px-3 py-1 rotate-3 ink-shadow-sm">
              {product.badge || "RISING SOON"}
            </span>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <p className="font-marker text-bloodglow text-base sm:text-lg mb-3 -rotate-1 inline-block">
              something wicked crawls out in
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl uppercase text-paper mb-8">
              {product.title}
              <BloodDrip />
            </h2>
            <div className="flex gap-3 sm:gap-4 mb-8">
              {units.map((u) => (
                <div
                  key={u.label}
                  data-testid={`countdown-${u.label.toLowerCase()}`}
                  className="border-2 border-blooddeep bg-pulp px-3 sm:px-5 py-4 text-center min-w-[70px] sm:min-w-[86px]"
                >
                  <p className="font-display text-3xl sm:text-4xl text-crimson crimson-glow tabular-nums">{u.value}</p>
                  <p className="font-courier text-[9px] uppercase tracking-widest text-paper/50 mt-1">{u.label}</p>
                </div>
              ))}
            </div>
            <p className="font-type text-sm text-paper/60 mb-6 max-w-md leading-relaxed">{product.description}</p>
            <button
              data-testid="countdown-preorder-button"
              onClick={() => {
                add(product);
                toast.success(`${product.title} rests in your coffin.`, { action: { label: "View Coffin", onClick: () => setOpen(true) } });
              }}
              className="flex items-center gap-2 font-impact uppercase tracking-wider bg-blooddeep text-bone border-2 border-black px-8 py-4 ink-shadow crimson-glow transition-all duration-150 hover:bg-crimson hover:-translate-y-0.5 active:translate-x-1 active:translate-y-1"
            >
              <Plus size={16} /> {product.preorder ? `Pre-Order — $${product.price.toFixed(2)}` : `Claim It — $${product.price.toFixed(2)}`}
            </button>
            {product.ships_by && (
              <p className="font-courier text-[10px] uppercase tracking-widest text-paper/40 mt-3">
                Ships by {product.ships_by}
              </p>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
